import { defaultApi } from '@/api';
import { components } from '@/types/api';
import { BudgetFieldType, InitiativeBudgetField, InitiativeBudgetData, BudgetSummary, TimePeriodBudget, SectionBudget } from '@/features/canvas/types/budget-type';

// Re-export types for convenience
export type { BudgetFieldType, InitiativeBudgetField, InitiativeBudgetData };

// Get budget with fields - ADD ROADMAP CONTEXT
export async function getInitiativeBudget({ 
  initiativeId,
  roadmapId 
}: { 
  initiativeId: number;
  roadmapId?: number; // Optional but recommended
}) {
  const params = roadmapId ? { roadmap_id: roadmapId } : {};
  const { data } = await defaultApi.get<{
    success: boolean;
    data: InitiativeBudgetData;
  }>(`/initiatives/${initiativeId}/budget`, { params });
  return data.data;
}

// Update budget values - ADD ROADMAP CONTEXT
export async function updateInitiativeBudget({
  initiativeId,
  budgetData,
  roadmapId,
}: {
  initiativeId: number;
  budgetData: {
    capex?: number;
    opex?: number;
    spend?: number;
    total_budget?: number;
    total_budget_override?: boolean;
    currency?: string;
  };
  roadmapId?: number; // Optional but recommended
}) {
  const payload = {
    ...budgetData,
    ...(roadmapId && { roadmap_id: roadmapId }),
  };
  
  const { data } = await defaultApi.put<{
    success: boolean;
    data: InitiativeBudgetData;
  }>(`/initiatives/${initiativeId}/budget`, payload);
  return data.data;
}

// Reset budget to zero
export async function resetInitiativeBudget({ initiativeId }: { initiativeId: number }) {
  const { data } = await defaultApi.post<{
    success: boolean;
    data: InitiativeBudgetData;
  }>(`/initiatives/${initiativeId}/budget/reset`);
  return data.data;
}

// Copy budget from one initiative to another
export async function copyInitiativeBudget({
  initiativeId,
  targetInitiativeId,
}: {
  initiativeId: number;
  targetInitiativeId: number;
}) {
  const { data } = await defaultApi.post<{
    success: boolean;
    data: InitiativeBudgetData;
  }>(`/initiatives/${initiativeId}/budget/copy`, {
    target_initiative_id: targetInitiativeId,
  });
  return data.data;
}

// Update budget field (numeric or text) - ALREADY HAS ROADMAP CONTEXT
export async function updateBudgetField({
  initiativeId,
  fieldId,
  fieldData,
  roadmapId,
}: {
  initiativeId: number;
  fieldId: number;
  fieldData: {
    value?: number;
    text_value?: string;
    disabled?: boolean;
    popup_show?: boolean;
  };
  roadmapId?: number; // Make it optional but recommended
}) {
  const payload = {
    ...fieldData,
    ...(roadmapId && { roadmap_id: roadmapId }),
  };

  const { data } = await defaultApi.put<{
    success: boolean;
    data: InitiativeBudgetField;
  }>(`/initiatives/${initiativeId}/budget/fields/${fieldId}`, payload);
  
  return data.data;
}

// Toggle field visibility - ADD ROADMAP CONTEXT
export async function toggleBudgetFieldVisibility({
  initiativeId,
  fieldId,
  roadmapId,
}: {
  initiativeId: number;
  fieldId: number;
  roadmapId?: number;
}) {
  const params = roadmapId ? { roadmap_id: roadmapId } : {};
  const { data } = await defaultApi.patch<{
    success: boolean;
    data: InitiativeBudgetField;
  }>(`/initiatives/${initiativeId}/budget/fields/${fieldId}/visibility`, params);
  return data.data;
}

// Get roadmap budget summary
export async function getRoadmapBudgetSummary({ 
  roadmapId, 
  currency = 'GBP',
  nodeIds 
}: { 
  roadmapId: number; 
  currency?: string;
  nodeIds?: number[];
}) {
  const params = new URLSearchParams();
  if (currency) params.append('currency', currency);
  if (nodeIds && nodeIds.length > 0) {
    nodeIds.forEach(id => params.append('node_ids[]', id.toString()));
  }
  
  const { data } = await defaultApi.get<{
    success: boolean;
    data: BudgetSummary;
  }>(`/roadmaps/${roadmapId}/budget/summary?${params.toString()}`);
  
  return data.data;
}

// Get budget by time period
export async function getBudgetByTimePeriod({ 
  roadmapId,
  currency = 'GBP',
  nodeIds 
}: { 
  roadmapId: number;
  currency?: string;
  nodeIds?: number[];
}) {
  const params = new URLSearchParams();
  if (currency) params.append('currency', currency);
  if (nodeIds && nodeIds.length > 0) {
    nodeIds.forEach(id => params.append('node_ids[]', id.toString()));
  }
  
  const { data } = await defaultApi.get<{
    success: boolean;
    data: TimePeriodBudget[];
  }>(`/roadmaps/${roadmapId}/budget/by-time-period?${params.toString()}`);
  
  return data.data;
}

// Get budget by section
export async function getBudgetBySection({ 
  roadmapId,
  currency = 'GBP',
  nodeIds 
}: { 
  roadmapId: number;
  currency?: string;
  nodeIds?: number[];
}) {
  const params = new URLSearchParams();
  if (currency) params.append('currency', currency);
  if (nodeIds && nodeIds.length > 0) {
    nodeIds.forEach(id => params.append('node_ids[]', id.toString()));
  }
  
  const { data } = await defaultApi.get<{
    success: boolean;
    data: SectionBudget[];
  }>(`/roadmaps/${roadmapId}/budget/by-section?${params.toString()}`);
  
  return data.data;
}

// Bulk update budgets
export async function bulkUpdateBudgets({
  budgets,
}: {
  budgets: Array<{
    initiative_id: number;
    capex?: number;
    opex?: number;
    spend?: number;
    total_budget?: number;
    total_budget_override?: boolean;
  }>;
}) {
  const { data } = await defaultApi.post<{
    success: boolean;
    data: {
      success: Array<{
        initiative_id: number;
        budget: InitiativeBudgetData;
      }>;
      errors: Array<{
        initiative_id: number | string;
        error: string;
      }>;
    };
  }>('/budgets/bulk-update', { budgets });
  return data.data;
}

// Create custom budget field - ADD ROADMAP CONTEXT
export async function createBudgetField({
  initiativeId,
  fieldData,
  roadmapId,
}: {
  initiativeId: number;
  fieldData: {
    type: 'big' | 'small';
    title: string;
    value?: number;
    text_value?: string;
    disabled?: boolean;
    popup_show?: boolean;
  };
  roadmapId?: number; // Optional but recommended
}) {
  const payload = {
    ...fieldData,
    ...(roadmapId && { roadmap_id: roadmapId }),
  };
  
  const { data } = await defaultApi.post<{
    success: boolean;
    data: InitiativeBudgetField;
  }>(`/initiatives/${initiativeId}/budget/fields`, payload);
  return data.data;
}

// Delete custom budget field
export async function deleteBudgetField({
  initiativeId,
  fieldId,
}: {
  initiativeId: number;
  fieldId: number;
}) {
  const { data } = await defaultApi.delete<{
    success: boolean;
  }>(`/initiatives/${initiativeId}/budget/fields/${fieldId}`);
  return data;
}

// Reorder budget fields
export async function reorderBudgetFields({
  initiativeId,
  fieldIds,
}: {
  initiativeId: number;
  fieldIds: number[];
}) {
  const { data } = await defaultApi.post<{
    success: boolean;
  }>(`/initiatives/${initiativeId}/budget/fields/reorder`, {
    field_ids: fieldIds,
  });
  return data;
}

// Get all budgets for a roadmap - THIS IS CRUCIAL FOR THE FIX
export async function getAllBudgetsForRoadmap({ roadmapId }: { roadmapId: number }) {
  const { data } = await defaultApi.get<{
    success: boolean;
    data: InitiativeBudgetData[];
  }>(`/roadmaps/${roadmapId}/budgets`);
  return data.data;
}

// Update field visibility for all initiatives in a roadmap (standard fields)
export async function updateAllInitiativesBudgetFieldVisibility({
  roadmapId,
  fieldType,
  visibility
}: {
  roadmapId: number;
  fieldType: 'capex' | 'opex' | 'spend';
  visibility: boolean;
}) {
  const { data } = await defaultApi.put<{
    success: boolean;
    data: {
      field_type: string;
      visibility: boolean;
      updated_count: number;
    };
  }>(`/roadmaps/${roadmapId}/budgets/field-visibility`, {
    field_type: fieldType,
    visibility
  });
  return data.data;
}

// Permanently delete budget custom field definition from ALL roadmaps
// This removes the field from dropdown_visibles, roadmap_visibles, and popup_visibles
// but preserves existing field data in initiatives that already have it
export async function permanentlyDeleteBudgetCustomFieldById({
  fieldId,
  roadmapId,
}: {
  fieldId: number;
  roadmapId: number;
}) {
  return defaultApi.delete(`/initiatives/budget/fields/${fieldId}/roadmap/${roadmapId}`);
}

// Update CUSTOM field visibility for all initiatives in a roadmap
export async function updateAllInitiativesCustomFieldVisibility({
  roadmapId,
  fieldType,
  fieldTitle,
  visibility
}: {
  roadmapId: number;
  fieldType: 'big' | 'small';
  fieldTitle: string;
  visibility: boolean;
}) {
  const { data } = await defaultApi.put<{
    success: boolean;
    data: {
      field_type: string;
      field_title: string;
      visibility: boolean;
      updated_count: number;
    };
  }>(`/roadmaps/${roadmapId}/budgets/custom-field-visibility`, {
    field_type: fieldType,
    field_title: fieldTitle,
    visibility
  });
  return data.data;
}

// NEW: Force sync budget fields after import
export async function syncBudgetFieldsAfterImport({
  roadmapId,
  initiativeId,
}: {
  roadmapId: number;
  initiativeId: number;
}) {
  // This endpoint would trigger a manual sync if needed
  const { data } = await defaultApi.post<{
    success: boolean;
  }>(`/roadmaps/${roadmapId}/initiatives/${initiativeId}/sync-budget-fields`);
  return data;
}
